import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, TemplateRef, ViewChild, ViewChildren, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  ActionIconDef, Grid as InavistaGrid, GridActionIconClickEvent, GridColumnInfo, GridConfig, GridHyperlinkClickEvent, GridRowCommitEvent,
  GridRowEditStartEvent, GridRowProblem, GridToolbarActionClickEvent,
} from '@inavista/components/grid';
import { IscCostTypes, IscCostTypesList, IscRuleModel, IscSubCostTypeList, SubCostTypes } from 'projects/service/src/app/modules/service-order/models/service-order.model';
import { ApiResponse, Companies, UserRoles } from 'projects/service/src/app/shared/models/app.model';
import { CommonService } from 'projects/service/src/app/shared/services/common.service';
import { ClaimStatuses } from 'projects/service/src/app/shared/models/claim.model';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ServiceOrderService } from 'projects/service/src/app/modules/service-order/services/service-order.service';
import { ACTION_NOT_PERMISSIBLE, CANNOT_CLAIM_TRANSACTION_MESSAGE, UPLOAD_RECEIPT_MESSAGE } from 'projects/service/src/app/shared/messages';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { CellEdit, DropdownEditor, NumberEditor, TextEditor } from 'projects/service/src/app/shared/utils/cell-editors';
import { exportInavistaGrid } from 'projects/service/src/app/shared/utils/inavista-grid-export';
import { LocalRowsGridApi, provideLocalGridRows } from 'projects/service/src/app/shared/utils/local-rows-grid-api';
import { MasterItemcodePopupComponent } from 'projects/shared/src/lib/components/master-itemcode-popup/master-itemcode-popup.component';
import {
  ADD_LINE_ACTION, APPROVE_ALL_ACTION, buildClaimLineActionIcons, buildClaimLinesGridConfig, CLAIM_LINES_URL, claimLineEditFields,
  claimLineFields, FLAG_ALL_ACTION, INCLUDE_TAX_ACTION, LINE_OPEN_FIELD, REJECT_ALL_ACTION,
} from './claim-lines.grid.config';

// Claim popup → Service Order Claims: the claim's lines on @inavista/components (claim-lines.grid.config.ts). The
// column list and each column's editor stay as they were, drawn with @inavista/components controls (CellEditorHost),
// so the price, total, tax, ISC-rule and business-rule logic below runs as before. The lines are the claim form's
// own `iscClaimDetails`, saved with the popup's Save.
@Component({
    selector: 'app-isc-so-claims',
    templateUrl: './isc-so-claims.component.html',
    styleUrls: ['./isc-so-claims.component.scss'],
    // The grid reads the claim form's lines, not an endpoint.
    providers: [...provideLocalGridRows()],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class IscSOClaimsComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroy$: Subject<null> = new Subject();
  private readonly gridRows = inject(LocalRowsGridApi);

  isTabValid: boolean = true;

  USER;

  referenceActivityObject: DropdownEditor;
  costTypeObject: DropdownEditor;
  subcostTypeObject: DropdownEditor;
  mileageObject: NumberEditor;
  itemDescriptionObject: TextEditor;
  discountAmountObject: NumberEditor;
  taxAmountObject: NumberEditor;
  taxPercentObject: NumberEditor;
  quantityObject: NumberEditor;
  priceObject: NumberEditor;
  totalAmountObject: NumberEditor;
  commentsObject: TextEditor;
  itemCodeObject: DropdownEditor;

  onItemUpdate = new Subject<string>();
  itemsList = [];
  rowServiceItemGroup: any;

  getPriceForItem: boolean = true;
  itemChangedFromAdvanceSearch: boolean = false;

  columns = [];

  isKingslynn: boolean = false;

  tempUnit: string = '';
  serviceItemGroup: any;
  coreItemGroup = ['450SVC'];
  itemGroup: any;
  selectedRowId: string;

  showItemAdvancedSearchPopup: boolean = false;

  // validServiceTypeCodes = ["WO", "WAR", "DEM", "CON", "TRN"];
  invalidServiceTypeCodes = ['TRN', 'POL', 'PAD', 'DEM', 'WSC', 'RFB', 'ECN'];

  @Input() claimsForm: UntypedFormGroup;
  @Input() iscRulesForSO: IscRuleModel;
  @Input() iscEmployeeDetails;
  @Input() costTypes = IscCostTypesList;
  @Input() taxRates = [];
  @Input() soReferenceActivities: any;
  @Input() referenceActivities: any;
  @Input() businessRules: any;
  @Input() businessRulesPassed: boolean = false;

  @Output() defaultComment = new EventEmitter();
  @Output() lineId = new EventEmitter();
  @Output() lineComment = new EventEmitter();
  @Output() businessRulesUpdated = new EventEmitter();

  @ViewChild('claimsGrid') claimsGrid: InavistaGrid;
  @ViewChildren('lineEditor') lineEditorTemplates: QueryList<TemplateRef<unknown>>;

  tempReferenceActivityDescription: any;
  serviceTypeCode: string;
  calloutRate: any;

  gridConfig: GridConfig = null;
  actionIcons: ActionIconDef[] = [];
  /** The cells drawn with the column's editor (CellEditorHost). */
  editFields: string[] = [];
  /** The editors of the open row, by field. */
  readonly openEditors = new Map<string, CellEdit>();
  /** A line is open for editing (the old claimsGrid.isEdit). */
  editing = false;
  /** The line Add Claim Item built; it joins the claim's lines when it is saved. */
  pendingNewLine: any = null;
  /** The open line and its hidden columns: Syncfusion drew an editor for every column, hidden ones included, and the
   *  rules below rely on those editors, so they are drawn out of sight. */
  hiddenEditRow: any = null;
  hiddenEditFields: string[] = [];
  private schema: GridColumnInfo[] = [];

  private readonly commonService = inject(CommonService);
  private readonly serviceOrderService = inject(ServiceOrderService);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly dialog = inject(MatDialog);
  // The Claims page is OnPush: state set outside a template event (timers, replies) must mark this view.
  private readonly cdr = inject(ChangeDetectorRef);

  showMasterItemCodePopup(eqData: any) {
    if (!eqData?.itemCode && !eqData?.item) return;
    this.dialog.open(MasterItemcodePopupComponent, {
      panelClass: 'master-itemcode-popup-dialog',
      width: '80%',
      height: '80%',
      data: eqData
    });
  }

  ngOnInit(): void {
    this.USER = this.commonService.USER;
    this.isKingslynn = this.USER.company === Companies.kingslynn

    this.getGridColumns();
    this.editFields = claimLineEditFields(this.columns);

    this.onItemUpdate
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      ).subscribe(value => {
        if (this.itemCodeObject) {
          this.itemCodeObject.showPopup();
          this.itemCodeObject.showSpinner();
        }
        this.getItemList({ item: value });
      });
  }

  // The editor templates are drawn: the grid is configured with them.
  ngAfterViewInit(): void {
    setTimeout(() => {
      this.schema = LocalRowsGridApi.schema([
        ...claimLineFields(this.columns).map(c => [c.field, c.type === 'number' || ['quantity', 'price', 'discountAmount', 'totalAmount', 'taxPercent'].includes(c.field) ? 'decimal' : c.type === 'datetime' ? 'datetime' : 'nvarchar'] as [string, string]),
        ['lineCommentCount', 'int'],
        [LINE_OPEN_FIELD, 'nvarchar'],
      ]);
      // The grid asks for its columns as soon as it is drawn.
      this.refreshGrid();
      this.showRoleBasedElementsInToolbar(this.claimsForm.value.status);
    });
  }

  // ---- The grid ------------------------------------------------------------------------------------------------

  /** The lines the grid shows: not deleted, each foreign-key column with its list's text, as Syncfusion showed it. */
  private lines(): any[] {
    return this.claimsForm.value.iscClaimDetails ?? [];
  }

  refreshGrid() {
    if (!this.schema.length) return;
    const fk = this.columns.filter(c => c.foreignKeyField && c.foreignKeyValue && c.dataSource);
    const rows = this.lines().filter(line => !line.isDeleted).map(line => {
      const row = { ...line, lineCommentCount: line.lineComments?.length ?? 0 };
      for (const c of fk) {
        const item = (c.dataSource as any[]).find(x => x[c.foreignKeyField] === line[c.field]);
        if (item) row[c.field] = item[c.foreignKeyValue];
      }
      return row;
    });
    this.gridRows.set(CLAIM_LINES_URL, this.schema, rows);
    this.claimsGrid?.refresh();
    this.cdr.markForCheck();
  }

  /** The claim line a grid row stands for (a new line: the one Add Claim Item built). */
  lineFor(row: any) {
    return row?.rowId == null ? this.pendingNewLine : this.lines().find(line => line.rowId === row.rowId);
  }

  getColumnByField(field: string) {
    return this.columns.find(c => c.field === field) ?? { validationRules: {} };
  }

  editDef(field: string): CellEdit {
    return this.getColumnByField(field).edit;
  }

  isColumnVisible(field: string): boolean {
    const own = this.columns.find(c => c.field === field);
    if (own?.showInColumnChooser === false) return own.visible !== false;
    const visibility = this.claimsGrid?.captureState().columnVisibility;
    if (visibility && field in visibility) return visibility[field];
    return !!own && own.visible !== false;
  }

  // The old editSettings: lines are edited while the claim is Open or Awaiting Information.
  private editLockReason(): string | null {
    return [ClaimStatuses.open, ClaimStatuses.awaitingInfo].includes(this.claimsForm.value.status)
      ? null : 'Claim lines can be changed while the claim is Open or Awaiting Information.';
  }

  // The Syncfusion validation the old grid ran on save: each shown column's rules on its editor's value.
  private validateLine(): GridRowProblem | null {
    for (const c of this.columns) {
      const rules = c.validationRules;
      if (!rules || !this.isColumnVisible(c.field)) continue;
      const open = this.openEditors.get(c.field);
      if (!open) continue;
      const value = open.read();
      if (rules.required && (value === null || value === undefined || value === '')) {
        return { column: c.field, message: `${c.headerText}: This field is required.` };
      }
    }
    return null;
  }

  // Add Claim Item, Approve / Reject / Flag All and Include Tax, by role and claim status, as before.
  // The claim's reviewer while In Progress (CSA), or a service manager / admin while Pending Approval.
  private reviews(status): boolean {
    const isReviewer = this.claimsForm.value.reviewer && this.claimsForm.value.reviewer === this.USER.employeeId;
    return this.USER.role === UserRoles.csa && status === ClaimStatuses.inProgress && isReviewer
      || this.USER.role === UserRoles.conwayServiceManager && status === ClaimStatuses.pendingApproval
      || this.USER.role === UserRoles.administratorRole && status === ClaimStatuses.pendingApproval;
  }

  showRoleBasedElementsInToolbar(status) {
    const reviews = this.reviews(status);
    const openForLines = [ClaimStatuses.open, ClaimStatuses.awaitingInfo].includes(status);
    const claims = this.commonService.roleClaims;
    const templates = this.lineEditorTemplates?.toArray() ?? [];
    this.gridConfig = buildClaimLinesGridConfig({
      columns: this.columns,
      editors: Object.fromEntries(this.editFields.map((field, i) => [field, templates[i]])),
      canExport: !!claims['AllGrid_Toolbar_Excel_Export']?.visible,
      canAdd: !!claims['Claims_Popup_ServiceOrderGrid_Toolbar_AddClaimLine']?.visible && openForLines,
      canApproveAll: !!claims['Claims_Popup_ServiceOrderGrid_Toolbar_ApproveAll']?.visible && reviews,
      canRejectAll: !!claims['Claims_Popup_ServiceOrderGrid_Toolbar_RejectAll']?.visible && reviews,
      canFlagAll: !!claims['Claims_Popup_ServiceOrderGrid_Toolbar_FlagAll']?.visible && reviews,
      showIncludeTax: !!claims['Claims_Popup_ServiceOrderGrid_Toolbar_IncludeTax']?.visible && openForLines,
      includeTax: !!this.claimsForm.value?.includeTax,
      includeTaxLockReason: this.claimsForm.value?.isNationalAccount && !this.iscRulesForSO?.iscTax
        ? 'This national account\'s ISC rules do not allow claiming tax.' : null,
      editLockReason: () => this.editLockReason(),
      validate: () => this.validateLine(),
    });
    this.setActions();
    this.cdr.markForCheck();
    setTimeout(() => this.refreshGrid());
  }

  // The old command column: Comment, then Approve / Reject / Flag for the reviewer, then Delete, by role.
  setActions() {
    const reviews = this.reviews(this.claimsForm.value.status);
    const claims = this.commonService.roleClaims;
    this.actionIcons = buildClaimLineActionIcons({
      canApprove: !!claims['Claims_Popup_ServiceOrderGrid_Action_Approve']?.visible && reviews,
      canReject: !!claims['Claims_Popup_ServiceOrderGrid_Action_Reject']?.visible && reviews,
      canFlag: !!claims['Claims_Popup_ServiceOrderGrid_Action_Flag']?.visible && reviews,
      canDelete: !!claims['Claims_Popup_ServiceOrderGrid_Action_Delete']?.visible,
      reviewLockReason: () => [ClaimStatuses.inProgress, ClaimStatuses.pendingApproval].includes(this.claimsForm.value.status)
        ? null : 'Claim lines can be reviewed while the claim is In Progress or Pending Approval.',
      deleteLockReason: row => {
        if (this.editing) return 'Finish editing the open line first.';
        if (![ClaimStatuses.open, ClaimStatuses.awaitingInfo].includes(this.claimsForm.value.status)) return 'Claim lines can be deleted while the claim is Open or Awaiting Information.';
        return this.lineFor(row)?.isManual ? null : 'Only lines added by hand can be deleted.';
      },
    });
  }

  // The row opened: the old actionBegin('beginEdit'), the editors draw, then the old actionComplete('beginEdit').
  onEditStart(event: GridRowEditStartEvent) {
    const rowData = this.lineFor(event.row);
    if (!rowData) return;
    // The previous line's editors are gone: no editor of this line may read them while its cells draw.
    this.referenceActivityObject = this.costTypeObject = this.subcostTypeObject = this.itemCodeObject = null;
    this.mileageObject = this.discountAmountObject = this.taxAmountObject = this.taxPercentObject = null;
    this.quantityObject = this.priceObject = this.totalAmountObject = null;
    this.itemDescriptionObject = this.commentsObject = null;
    this.editing = true;
    this.getPriceForItem = false;
    this.setItemCodeValidation(rowData.costType);
    if (rowData.costType === IscCostTypes.material) {
      this.getItemList(rowData);
    }
    this.tempReferenceActivityDescription = rowData.referenceActivityDescription;
    this.serviceTypeCode = rowData.serviceTypeCode;
    this.hiddenEditFields = this.editFields.filter(field => !this.isColumnVisible(field));
    this.hiddenEditRow = rowData;
    // The editors live outside the grid's draft: the line counts as changed from the start, so leaving it always
    // commits (or reverts) it, as the Syncfusion grid saved on leave, and `editing` can't stay set.
    const marked = this.claimsGrid?.setDraftValue(LINE_OPEN_FIELD, String(Date.now()));
    if (!marked) setTimeout(() => this.claimsGrid?.setDraftValue(LINE_OPEN_FIELD, String(Date.now())));
  }

  // Enter, the ✓ icon, a click elsewhere or the popup's Save: every open editor's value goes into the line, as the
  // Syncfusion grid did (with the old actionBegin('save')), then the old actionComplete('save').
  onCommit(commit: GridRowCommitEvent) {
    const target = this.lineFor(commit.row);
    if (!target) return;
    for (const [field, edit] of this.openEditors) target[field] = edit.read();
    target.referenceActivityDescription = this.tempReferenceActivityDescription;
    target.serviceTypeCode = this.serviceTypeCode;
    if (commit.isDraft) this.lines().push(target);
    this.pendingNewLine = null;
    this.hiddenEditRow = null;
    this.editing = false;
    this.updateTotalClaimAmounts();
    this.showUploadReceiptMessage();
    this.resetGlobals();
    this.isTabValid = true;
    this.refreshGrid();
  }

  // Esc or ✕. A new line was never added to the claim (before, a cancelled new line stayed on a saved claim).
  onRevert(event: { row: any; isDraft: boolean }) {
    this.editing = false;
    this.hiddenEditRow = null;
    if (event.isDraft) this.pendingNewLine = null;
    this.resetGlobals();
  }

  onToolbarAction(event: GridToolbarActionClickEvent) {
    switch (event.key) {
      case APPROVE_ALL_ACTION: this.updateAllClaimsStatuses(ClaimStatuses.approved); break;
      case REJECT_ALL_ACTION: this.updateAllClaimsStatuses(ClaimStatuses.rejected); break;
      case FLAG_ALL_ACTION: this.updateAllClaimsStatuses(ClaimStatuses.flagged); break;
      case INCLUDE_TAX_ACTION: this.includeTaxChange({ checked: !!event.checked }); break;
      case ADD_LINE_ACTION:
        this.claimsGrid?.saveBatch();
        if (this.editing) {
          this.commonService.showNotification('warning', 'Claim is already in edit mode!');
        } else {
          this.addItem();
        }
        break;
    }
  }

  onActionIconClick(event: GridActionIconClickEvent) {
    const rowData = this.lineFor(event.row);
    if (rowData) this.onCommandClick({ commandColumn: { title: event.iconKey }, rowData });
  }

  // Item and Description open the item, as before.
  onHyperlink(event: GridHyperlinkClickEvent) {
    const rowData = this.lineFor(event.row);
    if (rowData) this.showMasterItemCodePopup(rowData);
  }

  // Same file as before: the rows the grid shows, the visible columns, "Claims(<date>).xlsx".
  onExportRequested() {
    exportInavistaGrid(this.claimsGrid, 'Claims');
  }

  // ---- The claim lines (unchanged rules) -----------------------------------------------------------------------

  getItemList(rowData?) {
    const value = rowData?.item || '';
    this.commonService.getItems(value, 30, this.USER.company)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: ApiResponse) => {
        if (res && res.result && res.result.length) {
          res.result.map(item => {
            item.value = item.item;
            item.text = `${item.item} | ${item.itemDescription}`;
          });

          const existingItems = [];
          this.lines().forEach(transaction => {
            if (transaction.item !== this.itemCodeObject?.value) {
              existingItems.push(transaction.item);
            }
          });
          this.itemsList = res.result.filter(item => !existingItems.includes(item.value));
          if (this.itemCodeObject) {
            this.itemCodeObject.hideSpinner();
            this.itemCodeObject.dataSource = this.itemsList;
          }
        } else {
          this.itemsList = [];
          if (this.itemCodeObject) {
            this.itemCodeObject.hideSpinner();
            this.itemCodeObject.dataSource = [];
          }
        }
      });
  }

  updateTotalClaimAmounts() {
    let claimAmount = 0;
    let disputedAmount = 0;
    let netClaimAmount = 0;
    let taxAmount = 0;
    this.claimsForm.value.iscClaimDetails.forEach((claim, index) => {
      if (!claim.isDeleted) {
        if ([ClaimStatuses.rejected, ClaimStatuses.flagged].includes(claim.status)) {
          disputedAmount += claim.totalAmount;
        }
        claimAmount += claim.totalAmount;
        if (this.claimsForm.value.includeTax && ![ClaimStatuses.rejected, ClaimStatuses.flagged].includes(claim.status)) {
          taxAmount += claim.taxAmount ?? 0;
        }
      }
    }, 0);
    this.claimsForm.get('claimAmount').setValue(claimAmount);
    this.claimsForm.get('disputedAmount').setValue(disputedAmount);
    this.claimsForm.get('taxAmount').setValue(taxAmount);
    netClaimAmount = claimAmount > disputedAmount ? claimAmount - disputedAmount : 0;
    this.claimsForm.get('netClaimAmount').setValue(netClaimAmount);
    this.claimsForm.get('totalClaimAmount').setValue(netClaimAmount + taxAmount);
  }

  getGridColumns() {
    this.columns = [
      { field: 'rowId', headerText: 'Sr.No', type: 'number', isPrimaryKey: true, showInColumnChooser: true, width: 100, visible: true },
      { field: 'id', headerText: 'ID', type: 'number', format: 'n', textAlign: 'Left', width: 100, showInColumnChooser: false, visible: false },
      { field: 'claimId', headerText: 'claim Id', type: 'string', textAlign: 'Left', visible: false },
      {
        field: 'referenceActivityCode', headerText: 'Reference Activity', textAlign: 'Left', showInColumnChooser: false, validationRules: { required: true }, filter: { type: 'Excel' },
        foreignKeyValue: 'description', foreignKeyField: 'value', dataSource: this.soReferenceActivities, edit: this.editReferenceActivity()
      },
      {
        field: 'costType', headerText: 'Cost Type', type: 'string', width: 115, textAlign: 'Left', filter: { type: 'Excel' }, visible: true, showInColumnChooser: true,
        foreignKeyValue: 'text', foreignKeyField: 'value', dataSource: this.costTypes, edit: this.editCostType(), validationRules: { required: true }
      },
      {
        field: 'subCostType', headerText: 'Sub-Cost Type', width: 137, textAlign: 'Left', filter: { type: 'Excel' }, visible: true, showInColumnChooser: true,
        foreignKeyValue: 'text', foreignKeyField: 'value', dataSource: IscSubCostTypeList, edit: this.editSubCostType()
      },
      { field: 'comments', headerText: 'Comments', type: 'string', textAlign: 'Left', visible: true, showInColumnChooser: false, allowEditing: true, validationRules: { required: false }, edit: this.editComments() },
      { field: 'item', headerText: 'Item', width: 175, textAlign: 'Left', filter: { type: 'Excel' }, visible: true, showInColumnChooser: true, edit: this.editItem(), validationRules: { required: true } },
      { field: 'status', headerText: 'Status', width: 175, textAlign: 'Left', visible: false, filter: { type: 'Excel' }, showInColumnChooser: false },
      { field: 'description', headerText: 'Description', width: 175, textAlign: 'Left', edit: this.editItemDescription(), visible: true, showInColumnChooser: true },
      { field: 'quantity', headerText: 'Quantity', textAlign: 'Left', visible: true, showInColumnChooser: true, width: 110, format: 'n2', edit: this.editQuantity(), validationRules: { required: true } },
      { field: 'mileage', headerText: 'Distance (KM/MI)', textAlign: 'Right', type: 'number', showInColumnChooser: false, width: 130, format: 'n2', edit: this.editMileage(), validationRules: { required: false } },
      { field: 'price', headerText: 'Price', textAlign: 'Left', visible: true, showInColumnChooser: true, width: 90, format: 'n2', edit: this.editPrice(), validationRules: { required: true } },
      { field: 'discountAmount', headerText: 'Discount', width: 115, visible: true, showInColumnChooser: true, textAlign: 'Left', format: 'n2', edit: this.editDiscountAmount() },
      { field: 'totalAmount', headerText: 'Total Amount', width: 150, textAlign: 'Left', visible: true, showInColumnChooser: true, format: 'n2', edit: this.editTotalAmount() },
      { field: 'taxPercent', headerText: 'Tax %', width: 105, visible: true, showInColumnChooser: true, textAlign: 'Left', format: 'n2', edit: this.editTaxPercent() },
      { field: 'taxAmount', headerText: 'Tax Amount', type: 'number', width: 130, visible: true, showInColumnChooser: true, textAlign: 'Left', format: 'n2', edit: this.editTaxAmount(), validationRules: { required: true } },
      { field: 'origin', headerText: 'Origin', width: 175, textAlign: 'Left', visible: true, showInColumnChooser: true, allowEditing: false },
      { field: 'lastUpdatedDate', headerText: 'Last Edited', type: 'datetime', textAlign: 'Left', showInColumnChooser: true, allowEditing: false, width: 250, visible: false },
    ];
  };

  onCommandClick(args) {
    switch (args.commandColumn.title) {
      case 'Delete':
        this.commonService.showConfirmation('Are you sure? You want to delete this claim?').then(result => {
          if (result.isConfirmed) {
            if (args.rowData.id) {
              args.rowData.isDeleted = true;
            } else {
              const index = this.claimsForm.value.iscClaimDetails.findIndex(ex => ex.rowId === args.rowData.rowId);
              this.claimsForm.value.iscClaimDetails.splice(index, 1);
            }
            this.claimsForm.value.iscClaimDetails.sort((a, b) => a.isDeleted - b.isDeleted).map((ex, index) => { ex.rowId = index + 1; return ex; })
            this.refreshGrid();
            this.updateTotalClaimAmounts();
            this.updateBusinessRulesStatuses(this.claimsForm.value);
          }
        });
        break;
      case 'Approve':
        args.rowData.status = ClaimStatuses.approved;
        break;
      case 'Reject':
        args.rowData.status = ClaimStatuses.rejected;
        break;
      case 'Flag':
        args.rowData.status = ClaimStatuses.flagged;
        break;
      case 'Comment':
        const commentTemplate = "<div style='display:flex; background: #ffff0052'><div style='text-decoration: underline'>Reference:</div><div>&nbsp;Claim line no. " + args.rowData.rowId + "</div></div><br>"
        this.defaultComment.emit(commentTemplate)
        this.lineId.emit(args.rowData.id)
        this.lineComment.emit(args.rowData.lineComments)
        break;
      default: break;
    }
    this.refreshGrid();
    this.updateTotalClaimAmounts();
    this.updateBusinessRulesStatuses(this.claimsForm.value);
  }

  includeTaxChange(args) {
    this.claimsForm.get('includeTax').setValue(args.checked);
    this.updateTotalClaimAmounts();
  }

  // The lines shown (the old setCellValue reached only those, never deleted lines).
  updateAllClaimsStatuses(status) {
    this.lines().filter(claim => !claim.isDeleted).forEach(claim => {
      claim.status = status;
    });
    this.refreshGrid();
    this.updateTotalClaimAmounts();
    this.updateBusinessRulesStatuses(this.claimsForm.value);
  }

  addItem() {
    const newIndex = this.lines().filter(item => !item.isDeleted).length;
    this.pendingNewLine = this.setClaimsRow({}, newIndex);
    this.claimsGrid?.startDraft();
  }

  getMaxRowId(datasource) {
    if (datasource.length) {
      return Math.max.apply(Math, datasource.map(row => row.rowId)) || 0;
    } else {
      return 0;
    }
  }

  setClaimsRow(claim, index) {
    const referenceActivity = this.soReferenceActivities.length === 1 ? this.soReferenceActivities[0].referenceActivity : '';
    const referenceActivityDescription = this.soReferenceActivities.length === 1 ? this.soReferenceActivities[0].referenceActivityDescription : '';
    claim.rowId = this.getMaxRowId(this.lines()) + 1;
    claim.id = claim.id || 0;
    claim.claimIdRef = claim.claimIdRef || 0;
    claim.claimId = this.claimsForm.value.claimId || '';
    claim.comments = '';
    claim.costType = IscCostTypes.material;
    claim.subCostType = claim.subCostType || '';
    claim.item = claim.item || '';
    claim.description = claim.description || '';
    claim.quantity = claim.quantity || 1;
    claim.price = claim.price || 0;
    claim.discountAmount = claim.discountAmount || 0;
    claim.totalAmount = claim.totalAmount || 0;
    claim.taxPercent = claim.taxPercent || 0;
    claim.isDeleted = claim.isDeleted || false;
    claim.createdDate = claim.createdDate || null;
    claim.createdBy = claim.createdBy || null;
    claim.lastUpdatedDate = claim.lastUpdatedDate || null;
    claim.lastUpdatedBy = claim.lastUpdatedBy || null;
    claim.referenceActivityCode = referenceActivity || claim.referenceActivityCode || '';
    claim.referenceActivityDescription = referenceActivityDescription || claim.referenceActivityDescription || '';
    claim.status = claim.status || 'Open';
    claim.serviceTypeCode = claim.serviceTypeCode || '';
    claim.isFlagged = claim.isFlagged || false;
    claim.lineComments = claim.lineComments || [];
    claim.isManual = true;
    claim.origin = 'Manual';
    claim.mileage = 0;
    claim.addtMileageCharged = 0;
    return claim;
  }

  // The popup's Save: the open line is saved first (a line that fails its checks stays open), as endEdit did.
  validate() {
    this.claimsGrid?.saveBatch();
    this.isTabValid = !this.editing;
    return this.isTabValid;
  }

  showUploadReceiptMessage() {
    if (this.subcostTypeObject?.value && ![SubCostTypes.travelCalloutCharge, SubCostTypes.mileage].includes(this.subcostTypeObject.value)) {
      this.commonService.showNotification('info', UPLOAD_RECEIPT_MESSAGE);
    }
  }

  resetGlobals() {
    this.itemsList = [];
    this.serviceTypeCode = '';
    this.tempReferenceActivityDescription = '';
  }

  editReferenceActivity(): CellEdit {
    return {
      read: () => this.referenceActivityObject?.value,
      destroy: () => this.referenceActivityObject?.destroy(),
      open: args => {
        if (!this.claimsForm.value.isNationalAccount) {
          this.soReferenceActivities = this.soReferenceActivities.filter(x => x.description !== "Billable Service");
        }
        return this.referenceActivityObject = new DropdownEditor({
          dataSource: this.soReferenceActivities,
          fields: { value: 'value', text: 'description' },
          value: args.rowData.referenceActivityCode,
          allowFiltering: true,
          popupWidth: '250px',
          enabled: args.rowData.isManual,
          change: e => {
            this.tempReferenceActivityDescription = e.itemData?.description;
            this.serviceTypeCode = e.itemData?.serviceTypeCode;
            this.discountAmountObject && (this.discountAmountObject.enabled = this.isDiscountEnabled());
          }
        });
      }
    }
  }

  editCostType(): CellEdit {
    return {
      read: () => this.costTypeObject?.value,
      destroy: () => this.costTypeObject?.destroy(),
      open: args => {
        return this.costTypeObject = new DropdownEditor({
          dataSource: this.costTypes,
          fields: { value: 'value', text: 'text' },
          value: args.rowData.costType,
          popupWidth: '150px',
          enabled: args.rowData.isManual,
          change: event => {
            this.priceObject.enabled = this.isIscPriceEnabled(args.rowData);
            this.setItemCodeValidation(event.value);
            this.setClaimLineValues(event.value, args.rowData);
            this.discountAmountObject.enabled = this.isDiscountEnabled();
          }
        });
      }
    }
  }

  isIscPriceEnabled(rowData) {
    const isBillable = this.getRefActivityCategory(rowData.term) === 'Billable Service';
    if (!this.claimsForm.value.isNationalAccount && isBillable) { return true } else {
      return this.subcostTypeObject?.value && ![SubCostTypes.travelCalloutCharge, SubCostTypes.mileage].includes(this.subcostTypeObject.value);
    }
  }

  setItemCodeValidation(costType) {
    this.getColumnByField('item').validationRules['required'] = costType === IscCostTypes.material;
  }

  setClaimLineValues(costType: any, rowData) {
    this.subcostTypeObject.enabled = costType === IscCostTypes.other;
    this.itemCodeObject.enabled = costType === IscCostTypes.material;
    this.subcostTypeObject.value = null;
    this.itemCodeObject.value = null;
    this.itemDescriptionObject.value = null;
    if (costType === IscCostTypes.labor) {
      this.priceObject.value = this.claimsForm.value?.isNationalAccount ? this.iscEmployeeDetails.t2LaborRate : this.iscEmployeeDetails.t1LaborRate;
      this.itemDescriptionObject.value = 'Labor charge';
    }
    // The Item column's Advanced Search: Material only.
    this.itemCodeObject.actionVisible.set(![IscCostTypes.labor, IscCostTypes.other].includes(costType));
  }

  editSubCostType(): CellEdit {
    return {
      read: () => this.subcostTypeObject?.value,
      destroy: () => this.subcostTypeObject?.destroy(),
      open: args => {
        return this.subcostTypeObject = new DropdownEditor({
          dataSource: IscSubCostTypeList,
          fields: { value: 'value', text: 'text' },
          value: args.rowData.subCostType,
          popupWidth: '150px',
          enabled: args.rowData.costType === IscCostTypes.other && args.rowData.isManual,
          change: event => {
            const isNumber = this.costTypeObject.value === IscCostTypes.material || event.value && event.value !== SubCostTypes.travelCalloutCharge ? true : false;
            this.quantityObject.format = isNumber ? 'n' : 'n2';
            this.quantityObject.step = isNumber ? 1 : 0.1;
            this.quantityObject.decimals = isNumber ? 0 : 2;
            if (this.costTypeObject.value === IscCostTypes.other) {
              this.claimsForm?.value?.isNationalAccount && this.checkIscRulesForSubcost(event.value, args.rowData);
              this.itemDescriptionObject.value = event.itemData?.text || null;
              const isTravelCallout_mileage = [SubCostTypes.travelCalloutCharge, SubCostTypes.mileage].includes(event.value);
              this.priceObject.enabled = !isTravelCallout_mileage;
              this.mileageObject.enabled = args.rowData.isManual && event.value && event.value === SubCostTypes.travelCalloutCharge
              if (event.value === SubCostTypes.travelCalloutCharge)
                this.priceObject.value = this.claimsForm?.value?.isNationalAccount ? this.iscEmployeeDetails.t2CalloutRate : this.iscEmployeeDetails.t1CalloutRate;
              else if (event.value === SubCostTypes.mileage)
                this.priceObject.value = this.iscEmployeeDetails.mileageRate;

              this.getColumnByField('comments').validationRules['required'] = event.value && event.value !== SubCostTypes.travelCalloutCharge;
              this.discountAmountObject.enabled = this.isDiscountEnabled();
            }
          }
        });
      }
    }
  }

  editComments(): CellEdit {
    return {
      read: () => this.commentsObject?.value,
      destroy: () => this.commentsObject?.destroy(),
      open: args => {
        return this.commentsObject = new TextEditor({
          value: args.rowData.comments,
          enabled: args.rowData.isManual
        });
      }
    }
  }

  checkIscRulesForSubcost(subcostType, rowData) {
    subcostType === SubCostTypes.travelCalloutCharge && this.claimMultipleTripCharge(rowData);
    subcostType === SubCostTypes.travelCalloutCharge && this.billMultipleTripCharge(rowData);
    subcostType === SubCostTypes.local && this.claimLocalParts();
    subcostType === SubCostTypes.local && this.billLocalParts();
    subcostType === SubCostTypes.mileage && this.claimAdditionalMileage();
    subcostType === SubCostTypes.mileage && this.billAdditionalMileage();
    subcostType === SubCostTypes.freight && this.claimFreightCharge();
    subcostType === SubCostTypes.freight && this.billFreightCharge();
    subcostType === SubCostTypes.freight && this.claimTax();
  }

  // Kept as before (reported to Martin): the loop keeps only its last line's answer, and compares the line being
  // edited, not each line, with Travel Callout Charge.
  claimMultipleTripCharge(rowData) {
    let travelCalloutChargeExists = false;
    this.lines().forEach(claim => {
      travelCalloutChargeExists = claim.rowId !== rowData.rowId && this.subcostTypeObject.value === SubCostTypes.travelCalloutCharge;
    });
    travelCalloutChargeExists && !this.iscRulesForSO.claimMultipleTripCharge && this.commonService.showNotification('warning', CANNOT_CLAIM_TRANSACTION_MESSAGE, 'center', 5000);
  }

  billMultipleTripCharge(rowData) {
    let travelCalloutChargeExists = false;
    this.lines().forEach(claim => {
      travelCalloutChargeExists = claim.rowId !== rowData.rowId && this.subcostTypeObject.value === SubCostTypes.travelCalloutCharge
    });
    if (travelCalloutChargeExists && !this.iscRulesForSO.billMultipleTripCharge) {
      this.commonService.showNotification('warning', ACTION_NOT_PERMISSIBLE, 'center', 5000);
    }
  }

  claimLocalParts() {
    !this.iscRulesForSO.claimLocalPart && this.commonService.showNotification('warning', ACTION_NOT_PERMISSIBLE, 'center', 5000);
  }

  // Kept as before (reported to Martin): warns every time, whatever the rule says.
  billLocalParts() {
    this.commonService.showNotification('warning', ACTION_NOT_PERMISSIBLE, 'center', 5000);
  }

  claimAdditionalMileage() {
    !this.iscRulesForSO.claimAddtMileage && this.commonService.showNotification('warning', ACTION_NOT_PERMISSIBLE, 'center', 5000);
  }

  billAdditionalMileage() {
    !this.iscRulesForSO.billAddtMileage && this.commonService.showNotification('warning', ACTION_NOT_PERMISSIBLE, 'center', 5000);
  }

  claimFreightCharge() {
    !this.iscRulesForSO.claimFrieght && this.commonService.showNotification('warning', ACTION_NOT_PERMISSIBLE, 'center', 5000);

  }

  billFreightCharge() {
    !this.iscRulesForSO.billFrieght && this.commonService.showNotification('warning', ACTION_NOT_PERMISSIBLE, 'center', 5000);
  }

  claimTax() {
    !this.iscRulesForSO.iscTax && this.commonService.showNotification('warning', ACTION_NOT_PERMISSIBLE, 'center', 5000);
  }

  checkIscRulesForMileageItem(costType) {
    this.claimsForm.value?.isNationalAccount && costType === IscCostTypes.material && this.claimMileageItem();
    this.claimsForm.value?.isNationalAccount && costType === IscCostTypes.material && this.billMileageItem();
  }

  claimMileageItem() {
    !this.iscRulesForSO.addMileageItem && this.commonService.showNotification('warning', CANNOT_CLAIM_TRANSACTION_MESSAGE, 'center', 5000);
  }

  billMileageItem() {
    this.commonService.showNotification('warning', ACTION_NOT_PERMISSIBLE, 'center', 5000);
  }

  editItem(): CellEdit {
    return {
      read: () => this.itemCodeObject?.value,
      destroy: () => this.itemCodeObject?.destroy(),
      open: args => {
        this.selectedRowId = args.rowData?.rowId;
        const editor = this.itemCodeObject = new DropdownEditor({
          dataSource: this.itemsList,
          fields: { value: 'value', text: 'text' },
          value: args.rowData.item,
          allowFiltering: true,
          showClearButton: true,
          popupWidth: '350px',
          openOnFocus: true,
          enabled: args.rowData.isManual && this.costTypeObject?.value === IscCostTypes.material,
          filtering: e => { this.onItemUpdate.next(e.text) },
          action: { title: 'Advanced Search', iconClass: 'fas fa-info', click: () => { this.showItemAdvancedSearchPopup = true; } },
          change: event => {
            if (event.value && event.itemData) {
              this.getPriceForItem = event.isInteracted || this.itemChangedFromAdvanceSearch;
              const unit = this.tempUnit;
              if (event.itemData?.value !== 'LOCAL' && this.getPriceForItem) {
                this.getItemPrice(event.itemData?.value, unit, args.rowData, this.quantityObject.value);
              }
              if (event.value === 'Mileage') {
                this.checkIscRulesForMileageItem(event.value);
              }
              if (this.isKingslynn && event.itemData && this.coreItemGroup.includes(event.itemData.serviceItemGroup)) {
                this.commonService.showNotification('warning', 'Return Required', 'center', 5000);
              }
              this.itemDescriptionObject.value = event.itemData?.itemDescription ? event.itemData.itemDescription : null;
              this.serviceItemGroup = event.itemData?.serviceItemGroup;
              this.rowServiceItemGroup = event.itemData?.serviceItemGroup;
              this.itemGroup = event.itemData?.itemGroup;
              this.itemChangedFromAdvanceSearch = false;
            }
          }
        });
        // Advanced Search beside the list, for Material lines (the old icon).
        editor.actionVisible.set(args.rowData.costType === IscCostTypes.material);
        return editor;
      }
    }
  }

  onItemAdvancedSearchPopupClose(data: any) {
    this.itemChangedFromAdvanceSearch = true;
    this.rowServiceItemGroup = data?.serviceItemGroup;
    this.showItemAdvancedSearchPopup = false;
    if (!data) return;
    const pick = () => {
      if (this.isKingslynn && this.coreItemGroup.includes(data?.serviceItemGroup)) {
        this.commonService.showNotification('warning', 'Return Required', 'center', 5000);
      }
      this.itemDescriptionObject.value = data?.itemDescription;
      this.itemCodeObject.dataSource = [{ text: `${data?.item} | ${data?.itemDescription}`, value: data?.item, ...data }];
      this.itemCodeObject.value = data?.item;
    };
    if (this.editing) {
      pick();
    } else {
      // The line was closed meanwhile: it is opened again, then the pick applied.
      this.claimsGrid?.startEdit(this.selectedRowId);
      setTimeout(() => this.itemCodeObject && pick(), 400);
    }
  }

  getItemPrice(item, unit, rowData, quantity = 1) {
    const data = {
      company: this.USER.company,
      item: item,
      unit: 'ea',
      quantity,
      configuration: this.claimsForm.value.soTechnicianIscCode || this.iscEmployeeDetails.iscCode
    };
    this.serviceOrderService.getPrice(data)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: ApiResponse) => {
        if (res && res.isSuccessful && res.result) {
          this.priceObject.value = res.result.bookPrice ? +res.result.bookPrice.bookPriceValue?.toFixed(2) : 0;
          this.discountAmountObject.value = res.result?.discountAmount ? +res.result.discountAmount.discountAmountValue?.toFixed(2) : 0;
        } else {
          this.commonService.showNotification('error', "Error fetching price from Pricebook. Please contact support.");
        }
      }, (error: HttpErrorResponse) => {
        this.priceObject.value = 0;
        this.discountAmountObject.value = 0;
        this.commonService.showNotification('error', "Error fetching price from Pricebook. Please contact support.");
      });
  }

  getRefActivityCategory(refActivityCode) {
    const refActivity = this.referenceActivities?.find(act => act.value === refActivityCode);
    return refActivity ? refActivity.category : '';
  }

  editItemDescription(): CellEdit {
    return {
      read: () => this.itemDescriptionObject?.value,
      destroy: () => this.itemDescriptionObject?.destroy(),
      open: args => {
        return this.itemDescriptionObject = new TextEditor({
          value: args.rowData.description,
          enabled: false
        });
      }
    }
  }

  editPrice(): CellEdit {
    return {
      read: () => this.priceObject?.value,
      destroy: () => this.priceObject?.destroy(),
      open: args => {
        return this.priceObject = new NumberEditor({
          value: args.rowData.price,
          min: 0,
          enabled: args.rowData.isManual,
          focus: () => {
            this.priceObject.min = this.quantityObject?.value ? this.discountAmountObject.value / this.quantityObject.value || null : null;
          },
          change: (event) => {
            if (event.value) {
              const price = event.value;
              const qty = this.quantityObject ? this.quantityObject.value : args.rowData.quantity;
              let discount = this.discountAmountObject ? this.discountAmountObject.value : args.rowData.discountAmount;
              this.totalAmountObject.value = qty && qty * (price - discount) || 0;
              this.discountAmountObject.max = price * qty ? price * qty : null;
            } else {
              this.priceObject.value = 0;
            }
            this.discountAmountObject.max = event.value * this.quantityObject?.value;
          }
        });
      }
    }
  }

  isDiscountEnabled() {
    const isCostTypeOther = this.costTypeObject?.value === IscCostTypes.other;
    const isTravelCallout_mileage = [SubCostTypes.travelCalloutCharge, SubCostTypes.mileage].includes(this.subcostTypeObject?.value);
    return isCostTypeOther && this.subcostTypeObject?.value && !isTravelCallout_mileage;
  }

  editDiscountAmount(): CellEdit {
    return {
      read: () => this.discountAmountObject?.value,
      destroy: () => this.discountAmountObject?.destroy(),
      open: args => {
        return this.discountAmountObject = new NumberEditor({
          value: args.rowData.discountAmount || 0,
          min: 0,
          max: this.priceObject?.value,
          enabled: this.isDiscountEnabled(),
          focus: () => {
            const price = this.priceObject?.value,
              qty = this.quantityObject?.value;
            this.discountAmountObject.max = price * qty;
          },
          change: (event) => {
            const qty = this.quantityObject.value;
            const price = this.priceObject.value;
            if (event.value != null) {
              this.totalAmountObject.value = qty && price ? qty * (price - event.value) : 0;
            } else {
              this.discountAmountObject.value = 0;
            }
            this.priceObject.min = this.quantityObject?.value && event.value ? event.value / this.quantityObject.value || null : null;
          }
        });
      }
    }
  }

  editTaxPercent(): CellEdit {
    return {
      read: () => this.taxPercentObject?.value,
      destroy: () => this.taxPercentObject?.destroy(),
      open: args => {
        return this.taxPercentObject = new NumberEditor({
          value: args.rowData.taxPercent || 0,
          enabled: true,
          min: 0,
          max: 100,
          change: (event) => {
            const qty = this.quantityObject.value;
            const price = this.priceObject.value;
            const discount = this.discountAmountObject.value;

            if (event.value != null) {
              this.taxAmountObject.value = (qty * price - discount) * event.value / 100;
            }
          }
        });
      }
    }
  }

  editTaxAmount(): CellEdit {
    return {
      read: () => this.taxAmountObject?.value,
      destroy: () => this.taxAmountObject?.destroy(),
      open: args => {
        return this.taxAmountObject = new NumberEditor({
          value: args.rowData.taxAmount || 0,
          enabled: false, //args.rowData.isManual,
        });
      }
    }
  }

  editQuantity(): CellEdit {
    return {
      read: () => this.quantityObject?.value,
      destroy: () => this.quantityObject?.destroy(),
      open: args => {
        const isNumber = this.costTypeObject?.value === IscCostTypes.material || this.subcostTypeObject?.value && this.subcostTypeObject.value !== SubCostTypes.travelCalloutCharge ? true : false;
        return this.quantityObject = new NumberEditor({
          value: args.rowData.quantity,
          format: isNumber ? 'n' : 'n2',
          step: isNumber ? 1 : 0.1,
          min: 0,
          decimals: isNumber ? 0 : 2,
          enabled: args.rowData.isManual || args.rowData.status === ClaimStatuses.flagged,
          change: event => {
            const qty = event.value;
            const price = this.priceObject ? this.priceObject.value : args.rowData.price;
            let discount = this.discountAmountObject ? this.discountAmountObject.value : args.rowData.discountAmount;
            this.discountAmountObject.max = price && qty ? price * qty : null;
            this.totalAmountObject.value = qty && qty * (price - discount) || 0;
          }
        });
      }
    }
  }

  editMileage(): CellEdit {
    return {
      read: () => this.mileageObject?.value,
      destroy: () => this.mileageObject?.destroy(),
      open: args => {
        return this.mileageObject = new NumberEditor({
          value: args.rowData.mileage,
          format: 'n2',
          min: 0,
          decimals: 2,
          enabled: args.rowData.isManual && args.rowData.subCostType && args.rowData.subCostType === SubCostTypes.travelCalloutCharge,
          change: (event) => {
            const isBillable = this.getRefActivityCategory(this.referenceActivityObject?.itemData?.referenceActivityCode) === 'Billable Service';
            if (event.value != null) {
              if (isBillable && !this.claimsForm.value?.isNationalAccount) { // regular account and billable service
                // do nothing
              } else {
                this.calculateTotalForMileage(event.value, args.rowData);
              }
            }
          }
        });
      }
    }
  }

  calculateTotalForMileage(mileage, rowData) {
    const isBillable = this.getRefActivityCategory(this.referenceActivityObject?.itemData?.referenceActivityCode) === 'Billable Service';
    if (mileage) {
      let mileageExceededPrice = mileage > this.iscEmployeeDetails.mileageThreshold ? mileage * this.iscEmployeeDetails.mileageRate : undefined;
      const calloutRate = isBillable ? this.calloutRate : (mileageExceededPrice ?? this.iscEmployeeDetails.t1CalloutRate);
      this.priceObject.value = this.claimsForm.value?.isNationalAccount ? mileageExceededPrice ?? this.iscEmployeeDetails.t2CalloutRate : calloutRate;

      this.totalAmountObject.value = this.isAmountClaimable(rowData) ? this.quantityObject.value * this.priceObject.value - this.discountAmountObject.value : 0;
      this.claimsForm.value?.isNationalAccount && this.checkIscRulesForSubcost(this.subcostTypeObject.value, rowData);
    }
  }

  isAmountClaimable(rowData) {
    switch (this.subcostTypeObject.value) {
      case SubCostTypes.travelCalloutCharge: {
        const travelCalloutChargeExists = this.lines().findIndex(x => x.rowId !== rowData.rowId && !x.isDeleted && x.subCostType === SubCostTypes.travelCalloutCharge);
        if (travelCalloutChargeExists > -1) {
          return this.iscRulesForSO?.claimMultipleTripCharge ?? true;
        } else {
          return true;
        }
      }
      case SubCostTypes.local:
        return this.iscRulesForSO?.claimLocalPart ?? true;
      case SubCostTypes.mileage:
        return this.iscRulesForSO?.claimAddtMileage ?? true;
      case SubCostTypes.freight:
        return this.iscRulesForSO?.claimFrieght ?? true;
      case SubCostTypes.tax:
        return this.iscRulesForSO?.iscTax ?? true;
      default:
        return true;
    }
  }

  editTotalAmount(): CellEdit {
    return {
      read: () => this.totalAmountObject?.value,
      destroy: () => this.totalAmountObject?.destroy(),
      open: args => {
        return this.totalAmountObject = new NumberEditor({
          value: args.rowData.totalAmount,
          enabled: false,
          min: 0,
        });
      }
    }
  }

  updateBusinessRulesStatuses(claimsData, refActivities?) {
    this.businessRules = claimsData.iscBusinessRules;
    refActivities && (this.referenceActivities = refActivities);
    this.checkServiceTypeValid(claimsData.iscClaimDetails);
    this.checkInstallDateForINS(claimsData);
    this.checkInstallDateValid(claimsData);
    this.serviceDateInRange(claimsData.serviceCompletionDate, claimsData.submissionDate);
    this.checkTravelMiles(claimsData);
    this.checkClaimLimit();
    this.updateBusinessRulesInForm();
    this.claimsForm.value.businessRulesPassed = this.businessRules.findIndex(rule => rule.status === "Failed") > -1 ? false : true;
    this.businessRulesUpdated.emit();
  }

  updateBusinessRulesInForm() {
    const iscBusinessRulesArray = this.claimsForm.get('iscBusinessRules') as UntypedFormArray;
    const iscBusinessRulesData = this.businessRules;
    iscBusinessRulesArray.clear()
    iscBusinessRulesData ? iscBusinessRulesData.forEach((rule) => {
      const businessRule = this.fb.group(rule);
      iscBusinessRulesArray.push(businessRule);
    }) : [];
  }

  getTaxRate(costType) {
    if (this.taxRates) {
      const laborTypeKeys = {
        [IscCostTypes.labor]: 'LABMOBILE',
        [IscCostTypes.material]: 'MATERIAL',
        [IscCostTypes.other]: 'Other'
      }
      let taxRateItem = this.taxRates.find(taxRate => taxRate.productCategory === laborTypeKeys[costType]);
      if (taxRateItem && taxRateItem.taxRatePercentage) {
        return taxRateItem.taxRatePercentage;
      } else {
        return 0
      }
    } else {
      return 0;
    }
  }

  checkServiceTypeValid(claimItems) {
    const index = claimItems.findIndex(item => item.status !== ClaimStatuses.rejected && this.invalidServiceTypeCodes.includes(item.serviceTypeCode));
    this.businessRules[0].status = index === -1 ? "Passed" : "Failed";
  }

  checkInstallDateValid(claimsData) {
    this.businessRules[1].status = claimsData?.installationDate ? "Passed" : "Failed";
  }

  checkInstallDateForINS(claimsData) { // details tab check
    let installationDateForINSRuleStatus = "Failed";
    const hasServiceTypeCodeINS = claimsData.iscClaimDetails.findIndex(x => x.serviceTypeCode === 'INS') > -1;
    if (claimsData.callGroup === "INS" || hasServiceTypeCodeINS) {
      if (claimsData.installationDate && claimsData.serviceCompletionDate) {
        const installationDate = new Date(claimsData.installationDate);
        const serviceDate = new Date(claimsData.serviceCompletionDate);
        if (installationDate.setHours(0, 0, 0, 0) === serviceDate.setHours(0, 0, 0, 0)) {
          installationDateForINSRuleStatus = "Passed";
        }
      }
    } else {
      installationDateForINSRuleStatus = "Passed";
    }
    this.businessRules[2].status = installationDateForINSRuleStatus;
  }

  serviceDateInRange(serviceDate, submissionDate) {
    let serviceDateRuleStatus = "Failed";
    if (serviceDate && submissionDate) {
      serviceDate = new Date(serviceDate);
      const validDateRangeMax = new Date(submissionDate);
      let validDateRangeMin = new Date(submissionDate);
      validDateRangeMin.setDate(validDateRangeMin.getDate() - 30);
      if (validDateRangeMin <= serviceDate) {
        serviceDateRuleStatus = "Passed";
      }
    }
    this.businessRules[3].status = serviceDateRuleStatus;
  }

  checkTravelMiles(claimsData) {
    let totalMileage = 0;
    claimsData.iscClaimDetails.forEach(item => {
      if (item.status !== ClaimStatuses.rejected && item.costType === IscCostTypes.other && item.subCostType === SubCostTypes.travel) {
        totalMileage += item.quantity;
      }
    });
    this.businessRules[4].status = claimsData.country === "UNITED STATES" && totalMileage > 100 || claimsData.country === "CANADA" && totalMileage > 200 ? "Failed" : "Passed";
  }

  checkClaimLimit() {
    this.businessRules[5].status = this.claimsForm.value.totalClaimAmount > 4000 ? "Failed" : "Passed";
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.unsubscribe();
  }
}
